import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi } from '../../utils/api';
import { Spinner, Modal } from './shared';

const AUTO_REFRESH_INTERVAL = 10000;

function formatDuration(ms) {
    if (!ms || ms <= 0) return '0s';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
}

function formatBytes(bytes) {
    if (!bytes || bytes <= 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusBadge({ status }) {
    const styles = {
        running: 'bg-green-100 text-green-700',
        completed: 'bg-gray-100 text-gray-600',
        unknown: 'bg-yellow-100 text-yellow-700',
    };
    const dotStyles = {
        running: 'bg-green-500',
        completed: 'bg-gray-400',
        unknown: 'bg-yellow-500',
    };
    const s = status || 'unknown';
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[s] || styles.unknown}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${dotStyles[s] || dotStyles.unknown}`} />
            {s}
        </span>
    );
}

function StatCard({ label, value, sub }) {
    return (
        <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
        </div>
    );
}

function TaskRow({ task, onKill, onViewEvents }) {
    const [killing, setKilling] = useState(false);

    const handleKill = async () => {
        setKilling(true);
        try {
            await onKill(task.session_id);
        } finally {
            setKilling(false);
        }
    };

    return (
        <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
            <td className="px-4 py-2.5">
                <span className="font-mono text-xs text-gray-700">{task.session_id?.slice(0, 8)}...</span>
            </td>
            <td className="px-4 py-2.5 text-xs text-gray-600">{task.user_id || '-'}</td>
            <td className="px-4 py-2.5">
                <StatusBadge status={task.status || 'running'} />
            </td>
            <td className="px-4 py-2.5 text-xs text-gray-600 font-mono">{task.model || '-'}</td>
            <td className="px-4 py-2.5 text-xs text-gray-600">{formatDuration(task.duration_ms)}</td>
            <td className="px-4 py-2.5 text-xs text-gray-600 font-mono">{task.node_pid || '-'}</td>
            <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onViewEvents(task.session_id)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                        Events
                    </button>
                    <button
                        onClick={handleKill}
                        disabled={killing}
                        className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                    >
                        {killing ? 'Killing...' : 'Kill'}
                    </button>
                </div>
            </td>
        </tr>
    );
}

function ConnectionRow({ conn, onDisconnect }) {
    const [disconnecting, setDisconnecting] = useState(false);

    const handleDisconnect = async () => {
        setDisconnecting(true);
        try {
            await onDisconnect(conn.connection_id);
        } finally {
            setDisconnecting(false);
        }
    };

    return (
        <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
            <td className="px-4 py-2.5">
                <span className="font-mono text-xs text-gray-700">{conn.connection_id?.slice(0, 12)}...</span>
            </td>
            <td className="px-4 py-2.5 text-xs text-gray-600">{conn.user_id || '-'}</td>
            <td className="px-4 py-2.5 text-xs text-gray-600">
                {conn.subscribed_sessions?.length || 0} session(s)
            </td>
            <td className="px-4 py-2.5 text-xs text-gray-500">{conn.remote_addr || '-'}</td>
            <td className="px-4 py-2.5 text-xs text-gray-600 font-mono">{conn.node_pid || '-'}</td>
            <td className="px-4 py-2.5">
                <button
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                >
                    {disconnecting ? '...' : 'Disconnect'}
                </button>
            </td>
        </tr>
    );
}

function StreamRow({ stream, onCleanup }) {
    const [cleaning, setCleaning] = useState(false);

    const handleCleanup = async () => {
        setCleaning(true);
        try {
            await onCleanup(stream.session_id);
        } finally {
            setCleaning(false);
        }
    };

    return (
        <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
            <td className="px-4 py-2.5">
                <span className="font-mono text-xs text-gray-700">{stream.session_id?.slice(0, 8)}...</span>
            </td>
            <td className="px-4 py-2.5">
                <StatusBadge status={stream.status || 'unknown'} />
            </td>
            <td className="px-4 py-2.5 text-xs text-gray-600 font-mono">{stream.length}</td>
            <td className="px-4 py-2.5 text-xs text-gray-600">{formatBytes(stream.memory_bytes)}</td>
            <td className="px-4 py-2.5">
                <span className="font-mono text-[10px] text-gray-400">{stream.last_event_id?.slice(0, 12) || '-'}</span>
            </td>
            <td className="px-4 py-2.5">
                <button
                    onClick={handleCleanup}
                    disabled={cleaning}
                    className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                >
                    {cleaning ? '...' : 'Cleanup'}
                </button>
            </td>
        </tr>
    );
}

function EventsModal({ sessionId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!sessionId) return;
        setLoading(true);
        adminApi.getWSTaskEvents(sessionId)
            .then(setData)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [sessionId]);

    if (!sessionId) return null;

    return (
        <Modal onClose={onClose} title={`Events — ${sessionId.slice(0, 8)}...`} size="lg">
            {loading ? (
                <div className="flex justify-center py-8">
                    <Spinner size="md" text="Loading events..." />
                </div>
            ) : (
                <div>
                    <div className="flex items-center gap-4 mb-3 text-xs text-gray-500">
                        <span>Status: <StatusBadge status={data?.status || 'unknown'} /></span>
                        <span>Events: <span className="font-mono text-gray-700">{data?.events_count || 0}</span></span>
                        {data?.stream_memory_bytes && (
                            <span>Memory: <span className="font-mono text-gray-700">{formatBytes(data.stream_memory_bytes)}</span></span>
                        )}
                    </div>
                    <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase">#</th>
                                    <th className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase">Event ID</th>
                                    <th className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase">Type</th>
                                    <th className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase">Index</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data?.events || []).map((ev, i) => (
                                    <tr key={ev.event_id || i} className="border-t border-gray-100">
                                        <td className="px-3 py-1.5 text-[11px] text-gray-400 font-mono">{i + 1}</td>
                                        <td className="px-3 py-1.5 text-[11px] text-gray-500 font-mono">{ev.event_id?.slice(0, 12)}</td>
                                        <td className="px-3 py-1.5">
                                            <span className={`text-[11px] font-medium ${
                                                ev.type?.includes('start') ? 'text-blue-600' :
                                                ev.type?.includes('stop') ? 'text-gray-500' :
                                                ev.type?.includes('delta') ? 'text-green-600' :
                                                'text-gray-700'
                                            }`}>
                                                {ev.type}
                                            </span>
                                        </td>
                                        <td className="px-3 py-1.5 text-[11px] text-gray-500 font-mono">{ev.index ?? '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </Modal>
    );
}

const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'tasks', label: 'Agent Tasks' },
    { key: 'connections', label: 'Connections' },
    { key: 'streams', label: 'Redis Streams' },
];

export default function AdminWSMonitor() {
    const [tab, setTab] = useState('overview');
    const [overview, setOverview] = useState(null);
    const [tasks, setTasks] = useState(null);
    const [connections, setConnections] = useState(null);
    const [streams, setStreams] = useState(null);
    const [loading, setLoading] = useState(true);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [eventsSessionId, setEventsSessionId] = useState(null);
    const [broadcastMsg, setBroadcastMsg] = useState('');
    const [broadcasting, setBroadcasting] = useState(false);
    const timerRef = useRef(null);

    const fetchData = useCallback(async (showLoading = false) => {
        if (showLoading) setLoading(true);
        try {
            if (tab === 'overview' || showLoading) {
                const ov = await adminApi.getWSOverview();
                setOverview(ov);
            }
            if (tab === 'tasks') {
                const t = await adminApi.getWSTasks();
                setTasks(t);
            }
            if (tab === 'connections') {
                const c = await adminApi.getWSConnections();
                setConnections(c);
            }
            if (tab === 'streams') {
                const s = await adminApi.getWSStreams();
                setStreams(s);
            }
        } catch (e) {
            console.error('Failed to fetch WS monitor data:', e);
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => {
        fetchData(true);
    }, [fetchData]);

    useEffect(() => {
        if (autoRefresh) {
            timerRef.current = setInterval(() => fetchData(), AUTO_REFRESH_INTERVAL);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [autoRefresh, fetchData]);

    const handleKillTask = async (sessionId) => {
        try {
            await adminApi.killWSTask(sessionId);
            await fetchData();
        } catch (e) {
            console.error('Failed to kill task:', e);
        }
    };

    const handleDisconnect = async (connectionId) => {
        try {
            await adminApi.disconnectWSConnection(connectionId);
            await fetchData();
        } catch (e) {
            console.error('Failed to disconnect:', e);
        }
    };

    const handleCleanupStream = async (sessionId) => {
        try {
            await adminApi.cleanupWSStream(sessionId);
            await fetchData();
        } catch (e) {
            console.error('Failed to cleanup stream:', e);
        }
    };

    const handleBroadcast = async () => {
        if (!broadcastMsg.trim()) return;
        setBroadcasting(true);
        try {
            await adminApi.broadcastWSMessage(broadcastMsg.trim());
            setBroadcastMsg('');
        } catch (e) {
            console.error('Failed to broadcast:', e);
        } finally {
            setBroadcasting(false);
        }
    };

    const ws = overview?.ws_connections || {};
    const ag = overview?.agent_tasks || {};
    const rs = overview?.redis_streams || {};

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">WebSocket Monitor</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Real-time WebSocket connections, agent tasks & streams
                    </p>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={autoRefresh}
                        onChange={(e) => setAutoRefresh(e.target.checked)}
                        className="rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                    />
                    Auto-refresh
                </label>
            </div>

            <div className="flex items-center gap-1 mb-6 border-b border-gray-200">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            tab === t.key
                                ? 'border-gray-900 text-gray-900'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex justify-center py-20">
                    <Spinner size="lg" text="Loading..." />
                </div>
            ) : (
                <>
                    {tab === 'overview' && (
                        <div>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                                <StatCard label="WS Connections" value={ws.total || 0} sub={`${ws.unique_users || 0} unique users`} />
                                <StatCard label="Running Tasks" value={ag.running || 0} sub={`${ag.local_tasks || 0} on this node`} />
                                <StatCard label="Active Streams" value={rs.active_streams || 0} />
                                <StatCard label="Nodes" value={ws.by_node?.length || 0} />
                            </div>

                            {ws.by_node?.length > 0 && (
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
                                    <div className="px-4 py-3 border-b border-gray-100">
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Connections by Node</p>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                        {ws.by_node.map((node) => (
                                            <div key={node.pid} className="px-4 py-2.5 flex items-center justify-between">
                                                <span className="px-2 py-0.5 bg-gray-900 text-white text-xs font-mono rounded">PID {node.pid}</span>
                                                <span className="text-sm font-mono text-gray-700">{node.connections} connections</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                <div className="px-4 py-3 border-b border-gray-100">
                                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Broadcast Message</p>
                                </div>
                                <div className="px-4 py-3 flex items-center gap-3">
                                    <input
                                        type="text"
                                        value={broadcastMsg}
                                        onChange={(e) => setBroadcastMsg(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleBroadcast()}
                                        placeholder="Message to all connected clients..."
                                        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                                    />
                                    <button
                                        onClick={handleBroadcast}
                                        disabled={broadcasting || !broadcastMsg.trim()}
                                        className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50"
                                    >
                                        {broadcasting ? 'Sending...' : 'Broadcast'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {tab === 'tasks' && (
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                                <p className="text-sm font-medium text-gray-700">
                                    {tasks?.total || 0} task{(tasks?.total || 0) !== 1 ? 's' : ''}
                                </p>
                            </div>
                            {(tasks?.tasks || []).length === 0 ? (
                                <div className="px-4 py-10 text-center text-sm text-gray-400">No running tasks</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Session</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">User</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Status</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Model</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Duration</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Node</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(tasks?.tasks || []).map((task) => (
                                                <TaskRow
                                                    key={task.session_id}
                                                    task={task}
                                                    onKill={handleKillTask}
                                                    onViewEvents={setEventsSessionId}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'connections' && (
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <div className="px-4 py-3 border-b border-gray-100">
                                <p className="text-sm font-medium text-gray-700">
                                    {connections?.total || 0} connection{(connections?.total || 0) !== 1 ? 's' : ''}
                                </p>
                            </div>
                            {(connections?.connections || []).length === 0 ? (
                                <div className="px-4 py-10 text-center text-sm text-gray-400">No active connections</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Connection</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">User</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Sessions</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">IP</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Node</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(connections?.connections || []).map((conn) => (
                                                <ConnectionRow
                                                    key={conn.connection_id}
                                                    conn={conn}
                                                    onDisconnect={handleDisconnect}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'streams' && (
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                                <p className="text-sm font-medium text-gray-700">
                                    {streams?.total_streams || 0} stream{(streams?.total_streams || 0) !== 1 ? 's' : ''}
                                    {streams?.total_memory_bytes > 0 && (
                                        <span className="text-gray-400 font-normal"> &middot; {formatBytes(streams.total_memory_bytes)} total</span>
                                    )}
                                </p>
                            </div>
                            {(streams?.streams || []).length === 0 ? (
                                <div className="px-4 py-10 text-center text-sm text-gray-400">No active streams</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Session</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Status</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Events</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Memory</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Last Event</th>
                                                <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(streams?.streams || []).map((stream) => (
                                                <StreamRow
                                                    key={stream.session_id}
                                                    stream={stream}
                                                    onCleanup={handleCleanupStream}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {eventsSessionId && (
                <EventsModal
                    sessionId={eventsSessionId}
                    onClose={() => setEventsSessionId(null)}
                />
            )}
        </div>
    );
}
